import { Component } from '@theme/component';
import {
  isEarlyAccessLocked,
  resolveEarlyAccessButtonUnitPrice,
  resolveEarlyAccessCartItems,
  shouldRouteEarlyAccessCart,
  syncEarlyAccessBuyStack,
} from '@theme/early-access';
import { fetchConfig, preloadImage, onAnimationEnd, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent, StringingUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';
import { formatMoney } from '@theme/money-formatting';
import {
  getDiscountedPrices,
  isRegiosDiscount,
  buildRegiosPdpInputs,
  getRegiosSaleCents,
} from '@theme/regios-pricing';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    }
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  #getAddToCartButton() {
    return this.refs?.addToCartButton ?? this.querySelector('[ref="addToCartButton"]');
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    const addToCartButton = this.#getAddToCartButton();
    if (addToCartButton instanceof HTMLButtonElement) addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    const addToCartButton = this.#getAddToCartButton();
    if (addToCartButton instanceof HTMLButtonElement) addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const productForm = /** @type {ProductFormComponent | null} */ (this.closest('product-form-component'));
    if (productForm?.hasCompleteVariantSelection && !productForm.hasCompleteVariantSelection()) {
      return;
    }
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }
    const addToCartButton = this.#getAddToCartButton();
    if (!(addToCartButton instanceof HTMLButtonElement)) return;

    if (addToCartButton.dataset.puppet !== 'true') {
      const animationEnabled = this.dataset.addToCartAnimation === 'true';
      if (animationEnabled && !event.target.closest('.quick-add-modal')) {
        this.#animateFlyToCart();
      }
      this.animateAddToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const addToCartButton = this.#getAddToCartButton();
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    let flyToCartClass = addToCartButton.classList.contains('quick-add__button')
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const addToCartButton = this.#getAddToCartButton();
    if (!(addToCartButton instanceof HTMLButtonElement)) return;

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1); 
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} addToCartTextErrorDetail - Optional second required-field error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 * @property {HTMLElement | undefined} bulkThresholdMessage - The bulk inventory threshold message.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {boolean} */
  #variantChangeInProgress = false;

  /** @type {Array<{variantId: string, quantity: number}>} */
  #addToCartQueue = [];

  /** Guards against stale Regios results after a fast variant switch. */
  #regiosToken = 0;
  /** @type {number | undefined} Per-unit ATC price from the last stringing / custom bat update (cents). */
  #unitPriceOverride;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });
    this.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantitySelectorUpdate, { signal });
    this.closest('.shopify-section, dialog')?.addEventListener(ThemeEvents.stringingUpdate, this.#onStringingUpdate, {
      signal,
    });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });

    if (!this.#hasCompleteVariantSelection()) {
      this.#setChooseButtonState();
    } else {
      syncEarlyAccessBuyStack(this);
    }
    this.#initializeAddToCartButtonState();

    if (this.#getAddToCartButton()?.getAttribute('data-atc-state') === 'add') {
      this.#updateAddToCartButtonPrice();
    }
    this.#applyRegiosAddToCartPrice();
    this.#updateBulkInventoryThresholdMessage();
    this.#bindProductDrawNotifyNoop();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * The theme can call ProductFormComponent lifecycle hooks while Section Rendering is still
   * upgrading nested custom elements. In that window, an `<add-to-cart-component>` may be a plain
   * HTMLElement with no `enable` / `disable` methods, so fall back to the real button.
   * @param {Element | null | undefined} addToCartComponent
   * @param {boolean} disabled
   */
  #setAddToCartComponentDisabled(addToCartComponent, disabled) {
    if (!(addToCartComponent instanceof HTMLElement)) return;

    const method = disabled ? addToCartComponent.disable : addToCartComponent.enable;
    if (typeof method === 'function') {
      method.call(addToCartComponent);
      return;
    }

    const addToCartButton =
      addToCartComponent.refs?.addToCartButton ?? addToCartComponent.querySelector('[ref="addToCartButton"]');
    if (addToCartButton instanceof HTMLButtonElement) addToCartButton.disabled = disabled;
  }

  /**
   * Upcoming draw: Notify me is visual-only until Braze is wired to [data-product-draw-notify].
   */
  #bindProductDrawNotifyNoop() {
    if (this.dataset.productDraw !== 'true' || this.dataset.productDrawState !== 'upcoming') return;

    const notifyButton = this.querySelector('[data-product-draw-notify]');
    if (!(notifyButton instanceof HTMLButtonElement)) return;

    const { signal } = this.#abortController;
    notifyButton.addEventListener(
      'click',
      (event) => {
        event.preventDefault();
        event.stopPropagation();
      },
      { signal }
    );
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   * @returns {number} The cart quantity for the current variant
   */
  #updateCartQuantityFromData(cart) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value || !cart?.items) return 0;

    const cartItem = cart.items.find((item) => item.variant_id.toString() === variantIdInput.value.toString());
    const cartQty = cartItem ? cartItem.quantity : 0;

    // Use public API to update quantity selector
    const quantitySelector = /** @type {any | undefined} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);

    return cartQty;
  }

  /**
   * Fetches the current cart and updates the quantity display.
   * @returns {Promise<Cart | null>} The cart object, or null if fetch fails.
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return null;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      this.#updateCartQuantityFromData(cart);
      return cart;
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return null;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component') return;

    const cart = /** @type {Cart} */ (event.detail?.resource);
    if (cart?.items) {
      this.#updateCartQuantityFromData(cart);
    } else {
      await this.#fetchAndUpdateCartQuantity();
    }
  };

  /** @param {Event} event */
  handleSubmit(event) {
    event.preventDefault();

    const form = this.querySelector('form');
    const doogmaComponent = this.#getDoogmaComponent();
    const doogmaDetail = doogmaComponent?.getValidationMessage?.() || '';
    const requiredSelectionError =
      Theme.translations.required_selection_error || 'Please select all required attributes.';

    if ((form && !form.checkValidity()) || doogmaDetail) {
      this.#showAddToCartError(requiredSelectionError, false, doogmaDetail);
      return;
    }

    if (!this.#hasCompleteVariantSelection()) {
      this.#showAddToCartError(requiredSelectionError);
      return;
    }

    if (this.#variantChangeInProgress) {
      const intendedVariantId = this.#getIntendedVariantId();
      const quantity = this.#getQuantity();

      if (intendedVariantId) {
        this.#addToCartQueue.push({ variantId: intendedVariantId, quantity });
      }

      this.#getAddToCartComponent()?.animateAddToCart?.();
      return;
    }

    // Custom bats: the custom twin is untracked, so check stock + custom lines in the cart against
    // the stock units before adding either one (WIL26D2C-623).
    const batsComponent = this.#getBatsComponent();
    if (batsComponent) {
      this.#checkBatStock(batsComponent).then((hasStock) => {
        if (hasStock) this.#processAddToCart(undefined, undefined, event);
      });
      return;
    }

    // Doogma products must run the (async) Doogma save before the Shopify add-to-cart, so the
    // generated assets (thumb, print-ready, saved-design link) are captured as line-item properties.
    if (doogmaComponent?.isActive?.()) {
      doogmaComponent
        .saveDesign()
        .then(() => this.#processAddToCart(undefined, undefined, event))
        .catch((/** @type {Error} */ error) => {
          this.#showAddToCartError(
            error?.message || Theme.translations.required_selection_error || 'Please try again.',
            true
          );
        });
      return;
    }

    this.#processAddToCart(undefined, undefined, event);
  }

  /**
   * @returns {{ isActive?: () => boolean, saveDesign: () => Promise<void> } | null}
   */
  #getDoogmaComponent() {
    const productId = this.dataset.productId;
    const section = this.closest('.shopify-section, dialog');
    const selector = productId
      ? `doogma-customizer-component[data-product-id="${productId}"]`
      : 'doogma-customizer-component';

    return /** @type {any | null} */ (
      section?.querySelector(selector) ?? section?.querySelector('doogma-customizer-component') ?? null
    );
  }

  /** @returns {string | undefined} */
  #getIntendedVariantId() {
    return new URL(window.location.href).searchParams.get('variant') || this.refs.variantId?.value || undefined;
  }

  /** @returns {number} */
  #getQuantity() {
    return Number(this.refs.quantitySelector?.getValue?.()) || Number(this.dataset.quantityDefault) || 1;
  }

  #onQuantitySelectorUpdate = () => {
    this.#updateAddToCartButtonPrice(this.#unitPriceOverride);
    this.#updateBulkInventoryThresholdMessage();
  };

  /** @param {StringingUpdateEvent} event */
  #onStringingUpdate = (event) => {
    if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    // Kept for quantity changes. Only while stringing / personalization is on, so a stock
    // quantity change still goes through the early access and Regios button prices.
    this.#unitPriceOverride = event.detail.enabled ? event.detail.totalPrice : undefined;
    // Off: fall back to the button's own default (early access, Regios) rather than the catalog price.
    this.#updateAddToCartButtonPrice(this.#unitPriceOverride);
    this.#updateBulkInventoryThresholdMessage();
  };

  #getBulkInventoryThreshold() {
    const threshold = Number(this.dataset.bulkInventoryThreshold);
    return Number.isFinite(threshold) && threshold > 0 ? threshold : null;
  }

  #updateBulkInventoryThresholdMessage() {
    const thresholdMessage = this.refs.bulkThresholdMessage;
    if (!thresholdMessage) return;

    const threshold = this.#getBulkInventoryThreshold();
    if (threshold === null) {
      thresholdMessage.classList.add('hidden');
      return;
    }

    const quantity = this.#getQuantity();
    thresholdMessage.classList.toggle('hidden', quantity <= threshold);
  }

  /**
   * @param {number} [unitPriceOverride] - Optional per-unit price including string add-on (cents).
   */
  #updateAddToCartButtonPrice(unitPriceOverride) {
    const addToCartButton = this.#getAddToCartButton();
    const priceElement = addToCartButton?.querySelector('.add-to-cart-text__price');

    if (!priceElement) return;

    const hasOverridePrice = typeof unitPriceOverride === 'number' && Number.isFinite(unitPriceOverride);
    const selectedVariantId = (this.refs.variantId?.value || this.querySelector('input[name="id"]')?.value || '').trim();
    const earlyAccessUnitPrice = hasOverridePrice ? null : resolveEarlyAccessButtonUnitPrice(this, selectedVariantId);
    const defaultUnitPrice = Number(priceElement.getAttribute('data-default-unit-price'));

    let resolvedUnitPrice = Number(priceElement.getAttribute('data-unit-price'));

    if (Number.isFinite(earlyAccessUnitPrice)) {
      resolvedUnitPrice = Number(earlyAccessUnitPrice);
      priceElement.setAttribute('data-unit-price', String(resolvedUnitPrice));
    } else if (Number.isFinite(defaultUnitPrice) && defaultUnitPrice >= 0) {
      resolvedUnitPrice = defaultUnitPrice;
      priceElement.setAttribute('data-unit-price', String(resolvedUnitPrice));
    }

    const unitPrice = hasOverridePrice ? Number(unitPriceOverride) : resolvedUnitPrice;
    const moneyFormat = priceElement.getAttribute('data-money-format') || '{{amount}}';
    const currency = priceElement.getAttribute('data-currency') || Shopify.currency.active || '';
    const quantity = this.#getQuantity();

    if (!Number.isFinite(unitPrice) || !Number.isFinite(quantity)) return;

    const totalPrice = Math.max(0, unitPrice * Math.max(quantity, 1));
    priceElement.textContent = formatMoney(totalPrice, moneyFormat, currency);
  }

  /**
   * Swaps the add-to-cart button's unit price for the Regios (DOPP) discounted
   * price. Writes both `data-unit-price` and `data-default-unit-price` so
   * quantity recalculations keep the discounted basis; the early-access and
   * stringing overrides inside #updateAddToCartButtonPrice still win.
   */
  async #applyRegiosAddToCartPrice() {
    const token = ++this.#regiosToken;
    const reveal = () =>
      this.#getAddToCartButton()
        ?.querySelector('.add-to-cart-text__price-group')
        ?.classList.remove('regios-pending');

    if (!this.#getAddToCartButton()?.querySelector('.add-to-cart-text__price')) {
      reveal();
      return;
    }

    const variantId = (this.refs.variantId?.value || '').trim();
    if (!variantId) {
      reveal();
      return;
    }

    const inputs = buildRegiosPdpInputs(this.dataset.productId, variantId);
    const result = await getDiscountedPrices(inputs);

    if (token !== this.#regiosToken) return;

    try {
      if (!result || !isRegiosDiscount(result)) return;

      const saleCents = getRegiosSaleCents(result, inputs?.regularPriceInCents);
      if (saleCents == null) return;

      // Re-query: the button may have been morphed while awaiting the result.
      const priceElement = this.#getAddToCartButton()?.querySelector('.add-to-cart-text__price');
      if (!priceElement) return;

      priceElement.setAttribute('data-unit-price', String(saleCents));
      priceElement.setAttribute('data-default-unit-price', String(saleCents));
      // A late Regios result must not replace a stringing / custom bat price that is on.
      this.#updateAddToCartButtonPrice(this.#unitPriceOverride);
    } finally {
      reveal();
    }
  }

  /**
   * @param {string} [overrideVariantId]
   * @param {number} [overrideQuantity]
   * @param {Event} [event]
   */
  #processAddToCart(overrideVariantId, overrideQuantity, event) {
    if (this.#timeout) clearTimeout(this.#timeout);

    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    if (!overrideVariantId) {
      const anyButtonDisabled = Array.from(allAddToCartContainers).some((container) => {
        const addToCartButton = container.refs?.addToCartButton ?? container.querySelector('[ref="addToCartButton"]');
        return addToCartButton instanceof HTMLButtonElement && addToCartButton.disabled;
      });
      if (anyButtonDisabled) return;
    }

    const form = this.querySelector('form');
    if (!form) throw new Error('Product form element missing');

    if (!overrideVariantId && !this.#hasCompleteVariantSelection()) {
      this.#showAddToCartError(Theme.translations.required_selection_error || 'Please select all required attributes.');
      return;
    }

    if (!overrideVariantId && this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        for (const container of allAddToCartContainers) {
          this.#setAddToCartComponentDisabled(container, true);
        }

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity?.toString() || '');
        this.#showAddToCartError(errorMessage, true);

        setTimeout(() => {
          for (const container of allAddToCartContainers) {
            this.#setAddToCartComponentDisabled(container, false);
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const formData = new FormData(form);

    if (overrideVariantId) {
      formData.set('id', overrideVariantId);
    }
    if (overrideQuantity !== undefined) {
      formData.set('quantity', overrideQuantity.toString());
    }

    syncEarlyAccessBuyStack(this);

    if (shouldRouteEarlyAccessCart(this)) {
      const quantity = Number(formData.get('quantity')) || Number(this.dataset.quantityDefault) || 1;
      const displayVariantId = (overrideVariantId || formData.get('id')?.toString() || '').trim();
      const earlyAccessCart = resolveEarlyAccessCartItems(this, displayVariantId);

      if (earlyAccessCart && 'items' in earlyAccessCart) {
        // The batch payload is built from campaign purchase variants, not the PDP
        // form, so carry the per-market dates over or the UPS Ground shipping
        // restriction never sees these lines as Early Access.
        const earlyAccessDates = formData.get('properties[_early_access_dates]')?.toString().trim();

        this.#processBatchAddToCart(
          earlyAccessCart.items.map((item) => ({
            variantId: item.variantId,
            quantity: (Number(item.quantity) || 1) * quantity,
            ...(earlyAccessDates ? { properties: { _early_access_dates: earlyAccessDates } } : {}),
          }))
        );
        return;
      }

      this.#showAddToCartError(Theme.translations.sold_out || 'Sold out', true);
      return;
    }

    const stringingSubmission = this.#getStringingSubmission();
    this.#applyCaptureAtOrderForStringing(form, formData, stringingSubmission);

    if (stringingSubmission?.enabled) {
      for (const [propertyKey, propertyValue] of Object.entries(stringingSubmission.properties)) {
        formData.set(`properties[${propertyKey}]`, propertyValue);
      }

      if (stringingSubmission.customStrungVariantId) {
        formData.set('id', stringingSubmission.customStrungVariantId);
      }

      if (stringingSubmission.addStringLineItem && stringingSubmission.stringVariantId) {
        const racketVariantId = formData.get('id');
        const quantity = Number(formData.get('quantity')) || Number(this.dataset.quantityDefault) || 1;
        if (racketVariantId) {
          this.#processBatchAddToCart([
            {
              variantId: racketVariantId.toString(),
              quantity,
              properties: stringingSubmission.properties,
            },
            {
              variantId: stringingSubmission.stringVariantId,
              quantity,
              parentId: racketVariantId.toString(),
            },
          ]);
          return;
        }
      }
    }

    // Custom bats (WIL26D2C-623): the hidden Customization "With" variant carries the full
    // custom price. The page stays on the stock variant (OOS states follow stock), so the
    // swap happens here. Bats without that option keep properties on the stock variant.
    const batsSubmission = this.#getBatsSubmission(formData.get('id')?.toString());
    // Custom bats only: the checkout validation requires text, font and color on the custom line.
    if (batsSubmission?.enabled && batsSubmission.hasCustomVariants && !batsSubmission.complete) {
      this.#showAddToCartError(
        Theme.translations.required_selection_error || 'Please select all required attributes.'
      );
      return;
    }
    if (batsSubmission?.enabled && batsSubmission.hasCustomVariants) {
      // The custom twin is untracked: never buy it for a sold-out stock size.
      if (!batsSubmission.stockAvailable) {
        this.#showAddToCartError(Theme.translations.sold_out || 'Sold out', true);
        return;
      }
      if (!batsSubmission.customVariantId) {
        this.#showAddToCartError(batsSubmission.unavailableMessage, true);
        return;
      }
      formData.set('id', batsSubmission.customVariantId);
      if (batsSubmission.stockSku) {
        formData.set('properties[Stock Bat SKU]', batsSubmission.stockSku);
      }
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'application/json',
      },
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          this.#showAddToCartError(response.message, true);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');
          this.#hideAddToCartError();

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          // Get the added text from any add-to-cart button
          const anyAddToCartContainer = allAddToCartContainers[0];
          const anyAddToCartButton =
            anyAddToCartContainer?.refs?.addToCartButton ??
            anyAddToCartContainer?.querySelector?.('[ref="addToCartButton"]');
          if (anyAddToCartButton) {
            const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          // Fetch the updated cart to get the actual total quantity for this variant
          const cart = await this.#fetchAndUpdateCartQuantity();

          this.dispatchEvent(
            new CartAddEvent(cart ?? undefined, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        if (event) {
          cartPerformance.measureFromEvent('add:user-action', event);
        }
      });
  }

  /**
   * Tennis rackets share one PDP for stock and custom stringing.
   * Keep Capture at Order only when custom stringing on this form is purchased.
   * The nested-string batch payload does not submit the product form, so the
   * value has to be copied onto the racket line properties.
   * @param {HTMLFormElement} form
   * @param {FormData} formData
   * @param {{ enabled: boolean, properties: Record<string, string> } | null} stringingSubmission
   */
  #applyCaptureAtOrderForStringing(form, formData, stringingSubmission) {
    const captureInput = form.querySelector('[name="properties[_Capture at Order]"]');
    if (!(captureInput instanceof HTMLInputElement)) return;

    // Custom bats (WIL26D2C-623): only the personalized "With" variant carries it.
    if (captureInput.dataset.captureAtOrderRequiresCustomization === 'true') {
      const captureValue = captureInput.value.trim();
      const batsSubmission = this.#getBatsSubmission(formData.get('id')?.toString());
      // Bats without the Customization option still put personalization on the stock variant.
      const personalizedPurchase =
        batsSubmission?.enabled === true && (batsSubmission.customVariantId !== '' || !batsSubmission.hasCustomVariants);
      if (captureValue && personalizedPurchase) {
        formData.set('properties[_Capture at Order]', captureValue);
      } else {
        formData.delete('properties[_Capture at Order]');
      }
      return;
    }

    if (captureInput.dataset.captureAtOrderRequiresStringing !== 'true') return;

    const captureAtOrderValue = captureInput.value.trim();
    const scopedStringing = this.#getScopedStringingComponent();
    const customStringingSelected =
      scopedStringing != null &&
      typeof scopedStringing.getCartSubmission === 'function' &&
      scopedStringing.getCartSubmission()?.enabled === true;

    if (customStringingSelected && captureAtOrderValue && stringingSubmission?.properties) {
      stringingSubmission.properties['_Capture at Order'] = captureAtOrderValue;
      formData.set('properties[_Capture at Order]', captureAtOrderValue);
      return;
    }

    formData.delete('properties[_Capture at Order]');
  }

  /**
   * Stringing UI that belongs to this product form.
   * Does not search the rest of the document, so a stock quick add is not
   * treated as custom because the PDP toggle is on.
   * @returns {Element & { getCartSubmission?: () => { enabled?: boolean } | null } | null}
   */
  #getScopedStringingComponent() {
    const productId = this.dataset.productId;
    const section = this.closest('.shopify-section, dialog');
    if (!section) return null;

    const selector = productId
      ? `tennis-stringing-options-component[data-product-id="${productId}"]`
      : 'tennis-stringing-options-component';

    return section.querySelector(selector) ?? section.querySelector('tennis-stringing-options-component');
  }

  /**
   * Bats personalization UI that belongs to this product form.
   * Does not search the rest of the document, so a stock quick add is not
   * treated as custom because the PDP checkbox is on.
   * @param {string} [stockVariantId] - The variant id being submitted.
   * @returns {{ enabled: true, complete: boolean, hasCustomVariants: boolean, customVariantId: string, stockSku: string, stockAvailable: boolean, unavailableMessage: string } | null}
   */
  #getBatsSubmission(stockVariantId) {
    const component = this.#getBatsComponent();
    if (!component || typeof component.getCartSubmission !== 'function') return null;

    return component.getCartSubmission(stockVariantId);
  }

  /**
   * Bats personalization component scoped to this form's section or dialog (never the document).
   * @returns {(Element & { getCartSubmission?: (stockVariantId?: string) => any, getRemainingStock?: (stockVariantId: string, cartItems: any[]) => number | null, stockLimitMessage?: string }) | null}
   */
  #getBatsComponent() {
    const productId = this.dataset.productId;
    const section = this.closest('.shopify-section, dialog');
    if (!section) return null;

    const selector = productId
      ? `custom-bats-personalization-component[data-product-id="${productId}"]`
      : 'custom-bats-personalization-component';
    return section.querySelector(selector);
  }

  /**
   * @param {Element & { getRemainingStock?: (stockVariantId: string, cartItems: any[]) => number | null, stockLimitMessage?: string }} component
   * @returns {Promise<boolean>} false when the add would exceed the stock left for this size.
   */
  async #checkBatStock(component) {
    if (typeof component.getRemainingStock !== 'function') return true;

    const stockVariantId = this.querySelector('form')?.querySelector('input[name="id"]');
    if (!(stockVariantId instanceof HTMLInputElement) || !stockVariantId.value) return true;

    let cartItems = [];
    try {
      const response = await fetch('/cart.js');
      cartItems = (await response.json())?.items || [];
    } catch (error) {
      // Can't read the cart: let Shopify and the order-time inventory sync handle it.
      console.error('Failed to read the cart for the bat stock check:', error);
      return true;
    }

    const remaining = component.getRemainingStock(stockVariantId.value, cartItems);
    if (remaining == null || this.#getQuantity() <= remaining) return true;

    this.#showAddToCartError(
      component.stockLimitMessage || Theme.translations.sold_out || 'Sold out',
      true
    );
    return false;
  }

  /**
   * @returns {{ enabled: boolean, addStringLineItem: boolean, properties: Record<string, string>, stringVariantId?: string, customStrungVariantId?: string } | null}
   */
  #getStringingSubmission() {
    const productId = this.dataset.productId;
    const section = this.closest('.shopify-section, dialog');
    const selector = productId
      ? `tennis-stringing-options-component[data-product-id="${productId}"]`
      : 'tennis-stringing-options-component';

    let stringingComponent =
      section?.querySelector(selector) ?? section?.querySelector('tennis-stringing-options-component');

    if (!stringingComponent && productId) {
      stringingComponent = document.querySelector(
        `tennis-stringing-options-component[data-product-id="${productId}"]`
      );
    }

    if (!stringingComponent || typeof stringingComponent.getCartSubmission !== 'function') {
      return null;
    }

    return stringingComponent.getCartSubmission();
  }

  /**
   * @param {Array<{variantId: string, quantity: number, properties?: Record<string, string>, parentId?: string}>} items
   */
  #processBatchAddToCart(items) {
    if (items.length === 0) return;

    if (this.#timeout) clearTimeout(this.#timeout);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];
    for (const item of cartItemsComponents) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    }

    const payload = {
      items: items.map((item) => {
        const cartItem = {
          id: Number(item.variantId),
          quantity: item.quantity,
        };

        if (item.properties) {
          cartItem.properties = item.properties;
        }

        // Cart Ajax nested line: parent_id is the parent variant id when both
        // lines are added in the same request.
        if (item.parentId) {
          cartItem.parent_id = Number(item.parentId);
        }

        return cartItem;
      }),
      sections: cartItemComponentsSectionIds.join(','),
    };

    fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(this.id, response.message, response.description, response.errors)
          );

          this.#showAddToCartError(response.message, true);

          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
              productId: this.dataset.productId,
            })
          );
          return;
        }

        this.#hideAddToCartError();

        const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
          this.querySelectorAll('add-to-cart-component')
        );
        const anyAddToCartContainer = allAddToCartContainers[0];
        const anyAddToCartButton =
          anyAddToCartContainer?.refs?.addToCartButton ??
          anyAddToCartContainer?.querySelector?.('[ref="addToCartButton"]');
        if (anyAddToCartButton) {
          const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
          const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;
          this.#setLiveRegionText(addedText);
          setTimeout(() => this.#clearLiveRegionText(), SUCCESS_MESSAGE_DISPLAY_DURATION);
        }

        const cart = await this.#fetchAndUpdateCartQuantity();

        const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
        this.dispatchEvent(
          new CartAddEvent(cart ?? undefined, this.id, {
            source: 'product-form-component',
            itemCount: totalQuantity,
            productId: this.dataset.productId,
            sections: response.sections,
          })
        );
      })
      .catch((error) => {
        console.error(error);
      });
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * @param {string} message
   * @param {boolean} [autoHide=false]
   * @param {string} [detail=''] - Second line, used for a specific Doogma field error.
   */
  #showAddToCartError(message, autoHide = false, detail = '') {
    const { addToCartTextError, addToCartTextErrorDetail } = this.refs;
    if (!addToCartTextError) return;

    if (this.#timeout) clearTimeout(this.#timeout);

    const textElement = addToCartTextError.querySelector('[data-required-error-text]');
    if (textElement) {
      textElement.textContent = message;
    }

    addToCartTextError.classList.remove('hidden');

    const detailText = typeof detail === 'string' ? detail.trim() : '';
    if (addToCartTextErrorDetail) {
      const detailElement = addToCartTextErrorDetail.querySelector('[data-required-error-detail]');
      if (detailElement) detailElement.textContent = detailText;
      addToCartTextErrorDetail.classList.toggle('hidden', detailText === '');
    }

    this.#setLiveRegionText(detailText ? `${message} ${detailText}` : message);

    if (autoHide) {
      this.#timeout = setTimeout(() => {
        this.#hideAddToCartError();
      }, ERROR_MESSAGE_DISPLAY_DURATION);
    }
  }

  #hideAddToCartError() {
    const { addToCartTextError } = this.refs;
    if (!addToCartTextError) return;

    if (this.#timeout) clearTimeout(this.#timeout);
    addToCartTextError.classList.add('hidden');
    addToCartTextError.removeAttribute('aria-live');
    this.refs.addToCartTextErrorDetail?.classList.add('hidden');
    this.#clearLiveRegionText();
  }

  #getVariantPicker() {
    const section = this.closest('.shopify-section, dialog, product-card');
    if (!section) return null;
    return /** @type {{ hasCompleteSelection?: boolean } | null} */ (
      section.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"]`)
    );
  }

  /**
   * @returns {{ id?: number | string, price?: number, available?: boolean } | null}
   */
  #getSelectedVariantData() {
    const variantPicker = this.#getVariantPicker();
    const script = variantPicker?.querySelector('script[type="application/json"]');
    if (!script?.textContent) return null;

    try {
      return JSON.parse(script.textContent);
    } catch {
      return null;
    }
  }

  #initializeAddToCartButtonState() {
    const addToCartButton = this.#getAddToCartButton();
    if (!addToCartButton) return;

    const initialState = addToCartButton.getAttribute('data-atc-state');
    const hasVariantInForm = Boolean(this.refs.variantId?.value);
    const hasCompleteSelection = this.#hasCompleteVariantSelection();
    const variant = this.#getSelectedVariantData();

    const isPreselected =
      hasCompleteSelection || (hasVariantInForm && (initialState === 'add' || initialState === 'sold-out'));

    if (!isPreselected) {
      this.#setChooseButtonState();
      return;
    }

    const canAddToCart =
      initialState === 'add' || (variant != null && variant.available !== false && initialState !== 'sold-out');

    if (canAddToCart) {
      this.#setAddButtonState(variant);
      this.#setAddToCartComponentDisabled(this.#getAddToCartComponent(), false);
    } else {
      this.#setSoldOutButtonState();
      this.#setAddToCartComponentDisabled(this.#getAddToCartComponent(), true);
    }
  }

  #hasCompleteVariantSelection() {
    const variantPicker = this.#getVariantPicker();
    if (!variantPicker) return true;
    return variantPicker.hasCompleteSelection === true;
  }

  hasCompleteVariantSelection() {
    return this.#hasCompleteVariantSelection();
  }

  /** Shows the same "select all required attributes" error used by Add to Bag. */
  showRequiredSelectionError() {
    this.#showAddToCartError(Theme.translations.required_selection_error || 'Please select all required attributes.');
  }

  #getProductDrawEnterLabel() {
    if (this.dataset.productDraw !== 'true' || this.dataset.productDrawState !== 'active') {
      return null;
    }

    return Theme.translations.draw_enter || 'Enter draw';
  }

  #getAddToCartComponent() {
    return /** @type {AddToCartComponent | null} */ (this.querySelector('add-to-cart-component'));
  }

  #getAddToCartButton() {
    const addToCartComponent = this.#getAddToCartComponent();
    return (
      addToCartComponent?.refs?.addToCartButton ||
      addToCartComponent?.querySelector?.('[ref="addToCartButton"]') ||
      this.querySelector('add-to-cart-component [ref="addToCartButton"]') ||
      null
    );
  }

  /**
   * @param {{ price?: number } | null | undefined} [variant]
   */
  #setAddButtonState(variant) {
    const addToCartButton = this.#getAddToCartButton();
    if (!addToCartButton) return;

    const drawEnterLabel = this.#getProductDrawEnterLabel();
    if (drawEnterLabel) {
      addToCartButton.setAttribute('data-atc-state', 'draw');
      return;
    }

    addToCartButton.setAttribute('data-atc-state', 'add');
    addToCartButton.querySelector('.add-to-cart-icon')?.removeAttribute('hidden');
    addToCartButton.querySelector('.add-to-cart-text__price-group')?.removeAttribute('hidden');

    this.#ensureAddToCartPriceGroup(addToCartButton, variant);

    if (variant?.price != null) {
      const priceElement = addToCartButton.querySelector('.add-to-cart-text__price');
      priceElement?.setAttribute('data-unit-price', String(variant.price));
      priceElement?.setAttribute('data-default-unit-price', String(variant.price));
    }
  }

  #setSoldOutButtonState() {
    const addToCartButton = this.#getAddToCartButton();
    if (!addToCartButton) return;

    addToCartButton.setAttribute('data-atc-state', 'sold-out');
  }

  /**
   * Ensures the price group exists in the button (needed when the initial render was Choose state).
   * @param {HTMLButtonElement} addToCartButton
   * @param {{ price?: number } | null | undefined} [variant]
   */
  #ensureAddToCartPriceGroup(addToCartButton, variant) {
    if (addToCartButton.querySelector('.add-to-cart-text__price-group')) return;
    if (this.dataset.productDraw === 'true' && this.dataset.productDrawState === 'active') return;

    const addToCartText = addToCartButton.querySelector('.add-to-cart-text');
    if (!addToCartText) return;

    const unitPrice = variant?.price;
    if (unitPrice == null || !Number.isFinite(unitPrice)) return;

    const addToCartComponent = this.#getAddToCartComponent();
    const moneyFormat =
      addToCartComponent?.dataset.moneyFormat ||
      (typeof Shopify !== 'undefined' && Shopify.money_format) ||
      '{{amount}}';
    const currency =
      addToCartComponent?.dataset.currency ||
      (typeof Shopify !== 'undefined' && Shopify.currency?.active) ||
      '';

    const priceGroup = document.createElement('span');
    priceGroup.className = 'add-to-cart-text__price-group';

    const separator = document.createElement('span');
    separator.className = 'add-to-cart-text__separator';
    separator.setAttribute('aria-hidden', 'true');
    separator.textContent = '|';

    const priceElement = document.createElement('span');
    priceElement.className = 'add-to-cart-text__price';
    priceElement.setAttribute('data-unit-price', String(unitPrice));
    priceElement.setAttribute('data-default-unit-price', String(unitPrice));
    priceElement.setAttribute('data-money-format', moneyFormat);
    if (currency) priceElement.setAttribute('data-currency', currency);
    priceElement.textContent = formatMoney(unitPrice, moneyFormat, currency);

    priceGroup.append(separator, priceElement);
    addToCartText.append(priceGroup);
  }

  #setChooseButtonState() {
    const addToCartComponent = this.#getAddToCartComponent();
    const { acceleratedCheckoutButtonContainer } = this.refs;
    const addToCartButton = this.#getAddToCartButton();
    if (!addToCartComponent || !addToCartButton) return;

    const drawEnterLabel = this.#getProductDrawEnterLabel();

    this.#setAddToCartComponentDisabled(addToCartComponent, Boolean(drawEnterLabel));

    const addToCartText = addToCartButton.querySelector('.add-to-cart-text__content > span > span');
    if (addToCartText) {
      addToCartText.textContent = drawEnterLabel || Theme.translations.choose || 'Choose';
    }

    addToCartButton.setAttribute('data-atc-state', drawEnterLabel ? 'draw' : 'choose');
    acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');

    syncEarlyAccessBuyStack(this);
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    // Stale for the new variant. Stringing / custom bat components re-send it in a microtask.
    this.#unitPriceOverride = undefined;

    const hasCompleteSelection = this.#hasCompleteVariantSelection();
    const isComingSoon = this.dataset.comingSoon === 'true';
    const canAddToCart =
      hasCompleteSelection && event.detail.resource != null && event.detail.resource.available !== false && !isComingSoon;
    this.#hideAddToCartError();

    const { variantId } = this.refs;
    variantId.value = hasCompleteSelection ? event.detail.resource?.id ?? '' : '';

    this.#variantChangeInProgress = false;

    this.#flushQueuedAddToCart();

    // The back-in-stock and coming-soon CTAs replace the add-to-cart button, so the targeted
    // morphs below can't swap it. Morph the whole container when either variant uses one of them.
    const currentButtonsForBis = this.refs.productFormButtons;
    const newButtonsForBis = event.detail.data.html.querySelector('.product-form-buttons');
    const notifyFormSelector = 'back-in-stock-form, coming-soon-notify-form';
    const currentHasBackInStock = Boolean(currentButtonsForBis?.querySelector(notifyFormSelector));
    const newHasBackInStock = Boolean(newButtonsForBis?.querySelector(notifyFormSelector));
    if (currentButtonsForBis && newButtonsForBis && (currentHasBackInStock || newHasBackInStock)) {
      morph(currentButtonsForBis, newButtonsForBis);

      const { acceleratedCheckoutButtonContainer } = this.refs;
      if (canAddToCart) {
        acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      } else {
        acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      }
      this.#applyRegiosAddToCartPrice();
      return;
    }

    const addToCartComponent = this.#getAddToCartComponent();
    const currentAddToCartButton = this.#getAddToCartButton();
    const { acceleratedCheckoutButtonContainer } = this.refs;

    // Update state and text for add-to-cart button
    if (!currentAddToCartButton && !acceleratedCheckoutButtonContainer) return;

    // Update the button state
    if (!canAddToCart) {
      this.#setAddToCartComponentDisabled(addToCartComponent, true);
    } else {
      this.#setAddToCartComponentDisabled(addToCartComponent, false);
    }

    const productFormSelector = `product-form-component[data-product-id="${this.dataset.productId}"]`;
    const newAddToCartButton = event.detail.data.html.querySelector(
      `${productFormSelector} add-to-cart-component [ref="addToCartButton"]`
    );
    if (newAddToCartButton && currentAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    if (!hasCompleteSelection) {
      this.#setChooseButtonState();
    } else if (canAddToCart) {
      this.#setAddButtonState(event.detail.resource);
      acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
    } else {
      const drawEnterLabel = this.#getProductDrawEnterLabel();
      currentAddToCartButton?.setAttribute('data-atc-state', drawEnterLabel ? 'draw' : 'add');
      if (event.detail.resource == null || event.detail.resource.available == false) {
        acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else if (!isEarlyAccessLocked(this)) {
        acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
      this.#setSoldOutButtonState();
      acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      // Ensure icon/price visibility is restored when returning from choose/draw state.
      if (!drawEnterLabel) {
        currentAddToCartButton?.querySelector('.add-to-cart-icon')?.removeAttribute('hidden');
        currentAddToCartButton?.querySelector('.add-to-cart-text__price-group')?.removeAttribute('hidden');
      }
      if (event.detail.resource == null || event.detail.resource.available == false) {
        acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }

    if (canAddToCart) {
      this.#updateAddToCartButtonPrice();
    }
    this.#applyRegiosAddToCartPrice();

    // Set the data attribute for the product variant media if it exists
    if (canAddToCart && event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      if (productVariantMedia) {
        this.#getAddToCartComponent()?.setAttribute(
          'data-product-variant-media',
          productVariantMedia + '&width=100'
        );
      }
    }

    // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
    const {
      quantityRules,
      pricePerItem,
      quantitySelector,
      productFormButtons,
      quantityLabel,
      quantitySelectorWrapper,
    } = this.refs;

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
    }

    const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    const newPricePerItem = event.detail.data.html.querySelector('price-per-item');
    const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

    if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');

      if (productFormButtons && newProductFormButtons) {
        morph(productFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          // Temporarily set the old value so updateConstraints can snap it properly
          this.refs.quantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          this.refs.quantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
      const morphTargets = [
        ['.quantity-label', quantityLabel, quantitySelector],
        ['.quantity-rules', quantityRules, this.refs.productFormButtons],
        ['price-per-item', pricePerItem, quantitySelectorWrapper],
      ];

      for (const [selector, currentElement, fallback] of morphTargets) {
        this.#morphOrUpdateElement(currentElement, event.detail.data.html.querySelector(selector), fallback);
      }
    }

    // Morph volume pricing if it exists
    const currentVolumePricing = this.refs.volumePricing;
    const newVolumePricing = event.detail.data.html.querySelector('volume-pricing');
    this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);

    const hasB2BFeatures =
      quantityRules || newQuantityRules || pricePerItem || newPricePerItem || currentVolumePricing || newVolumePricing;

    syncEarlyAccessBuyStack(this);

    if (!hasB2BFeatures) return;

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /** @param {import('./events').VariantSelectedEvent} _event */
  #onVariantSelected = (_event) => {
    this.#variantChangeInProgress = true;
  };

  #flushQueuedAddToCart() {
    if (this.#addToCartQueue.length === 0) return;

    const queuedItems = [...this.#addToCartQueue];
    this.#addToCartQueue = [];

    /** @type {Map<string, number>} */
    const queuedQuantitiesByVariant = new Map();
    for (const item of queuedItems) {
      const variantId = item.variantId?.toString().trim();
      if (!variantId) continue;
      const quantity = Number(item.quantity) || 1;
      queuedQuantitiesByVariant.set(variantId, (queuedQuantitiesByVariant.get(variantId) || 0) + quantity);
    }

    const consolidatedItems = Array.from(queuedQuantitiesByVariant.entries()).map(([variantId, quantity]) => ({
      variantId,
      quantity,
    }));

    if (consolidatedItems.length === 0) return;

    // Preserve special add-to-cart flows (early access, stringing, validations)
    // when we can resolve the queue to a single variant request.
    if (consolidatedItems.length === 1) {
      const item = consolidatedItems[0];
      if (!item) return;
      this.#processAddToCart(item.variantId, item.quantity);
      return;
    }

    // Multiple queued variants are submitted in a single cart/add payload to avoid
    // redundant concurrent requests and queue replay race conditions.
    this.#processBatchAddToCart(consolidatedItems);
  }
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
